import React from "react";
import { Button } from "reactstrap";
import Select from "react-select";
import NavHeader from "./NavHeader";
import { UMBRAL_PRESELECCION } from "./importFixture";
import { catalogoDeEquipos, resolverEstructura, armarZonas, claveDeEquipo } from "./importEstructura";

// Previsualización de la estructura importada desde Excel, antes de tocar el torneo.
//
// Lo que se va a crear es una rueda nueva a continuación de las que ya están, con una
// zona por cada solapa del archivo, y los equipos que falten se suman a la lista
// general del torneo con su lista de fe. Los nombres que no coinciden exacto con
// ningún equipo del sistema los resuelve el usuario acá.
export default function ImportEstructuraScreen({
  zonas = [],
  ruedaNombre,
  equipos = [],
  teams = [],
  onConfirm,
  onBack,
}) {
  const catalogo = React.useMemo(() => catalogoDeEquipos(equipos, teams), [equipos, teams]);
  const resolucion = React.useMemo(() => resolverEstructura(zonas, catalogo), [zonas, catalogo]);

  // Resolución manual de los nombres sin reconocer: { [clave]: teamId }.
  // Las sugerencias fuertes vienen ya elegidas; las dudosas se muestran sin elegir para
  // que el usuario tenga que decidir.
  const [ajustes, setAjustes] = React.useState({});
  React.useEffect(() => {
    const iniciales = {};
    resolucion.forEach((zona) =>
      zona.equipos.forEach((equipo) => {
        if (equipo.sugerencia && equipo.sugerencia.score >= UMBRAL_PRESELECCION) {
          iniciales[claveDeEquipo(zona, equipo)] = equipo.sugerencia.id;
        }
      })
    );
    setAjustes(iniciales);
  }, [resolucion]);

  const zonasFinales = armarZonas(resolucion, ajustes);
  const porClave = Object.fromEntries(zonasFinales.map((z) => [z.clave, z]));

  const nombreDeEquipo = (teamId) => {
    const c = catalogo.find((x) => x.id === teamId);
    return c ? c.nombre : "—";
  };

  const idsEnTorneo = new Set(teams.map((t) => String(t.id)));
  const idsAImportar = zonasFinales.flatMap((z) => z.teamIds);
  const nuevos = idsAImportar.filter((id) => !idsEnTorneo.has(id));
  const sinResolver = resolucion.reduce(
    (total, zona) => total + zona.equipos.filter((e) => !e.equipoId && !ajustes[claveDeEquipo(zona, e)]).length,
    0
  );
  const repetidos = zonasFinales.flatMap((z) => z.repetidos);

  // Los equipos que no están en el padrón no se pueden crear desde acá: se dan de alta
  // en Equipos y recién ahí se los puede sumar al torneo.
  // Igual que en la importación de partidos: un doble toque creaba las zonas dos veces.
  const [confirmando, setConfirmando] = React.useState(false);
  const confirmadoRef = React.useRef(false);

  const confirmar = () => {
    if (confirmadoRef.current) return;
    confirmadoRef.current = true;
    setConfirmando(true);
    onConfirm(zonasFinales.filter((z) => z.teamIds.length > 0).map((z) => ({ name: z.name, teamIds: z.teamIds })));
  };

  const subtitulo = [zonas.length + " zonas leídas", ruedaNombre].filter(Boolean).join(" · ");

  return (
    <div className="screen-slide">
      <NavHeader title="Importar Estructura" subtitle={subtitulo} onBack={onBack} />

      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="sub-section-title">Resumen</div>
          <ul className="mb-0 pl-3" style={{ fontSize: "0.875rem" }}>
            <li>
              Se agrega <strong>{ruedaNombre}</strong> con{" "}
              <strong>{zonasFinales.filter((z) => z.teamIds.length > 0).length}</strong> zonas.
            </li>
            <li>
              <strong>{idsAImportar.length}</strong> equipos repartidos en las zonas.
            </li>
            <li>
              <strong>{nuevos.length}</strong> equipos se suman a la lista general del torneo (con su lista de fe).
            </li>
            {sinResolver > 0 && (
              <li className="text-warning">
                {sinResolver} nombres sin reconocer: si no los resolvés, esos equipos no se cargan.
              </li>
            )}
            {repetidos.length > 0 && (
              <li className="text-warning">{repetidos.length} equipos que caen en más de una zona.</li>
            )}
          </ul>
        </div>
      </div>

      {sinResolver > 0 && (
        <div className="mb-3">
          <div className="sub-section-title">Equipos sin reconocer</div>
          <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Estos nombres del archivo no coinciden con ningún equipo cargado. Puede ser un error de
            tipeo o una abreviatura. Elegí el equipo que corresponde o dejalos sin cargar. Si el
            equipo todavía no existe en el sistema, primero hay que darlo de alta en Equipos.
          </p>
          {resolucion.map((zona) =>
            zona.equipos
              .filter((equipo) => !equipo.equipoId)
              .map((equipo) => {
                const clave = claveDeEquipo(zona, equipo);
                const elegido = ajustes[clave];
                const opciones = equipo.ranking.map((c) => ({
                  value: c.id,
                  label: equipo.sugerencia && equipo.sugerencia.id === c.id ? c.nombre + " (sugerido)" : c.nombre,
                }));
                return (
                  <div className="card mb-2" key={clave}>
                    <div className="card-body py-2">
                      <div className="d-flex justify-content-between" style={{ fontSize: "0.8125rem" }}>
                        <span className="font-weight-bold">{equipo.nombreExcel}</span>
                        <span className="text-muted">{zona.nombre}</span>
                      </div>
                      {equipo.sugerencia && (
                        <p className="text-muted mb-2" style={{ fontSize: "0.8125rem" }}>
                          Se parece a <strong>{equipo.sugerencia.nombre}</strong>
                          {equipo.sugerencia.score < UMBRAL_PRESELECCION && " — confirmá si es el mismo equipo."}
                        </p>
                      )}
                      <Select
                        isSearchable={true}
                        isClearable={true}
                        placeholder="Elegí el equipo..."
                        classNamePrefix="react-select"
                        value={opciones.find((o) => o.value === elegido) || null}
                        onChange={(op) =>
                          setAjustes((prev) => ({ ...prev, [clave]: op ? op.value : undefined }))
                        }
                        options={opciones}
                        noOptionsMessage={() => "Sin equipos"}
                      />
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}

      {repetidos.length > 0 && (
        <div className="mb-3">
          <div className="sub-section-title">Equipos repetidos</div>
          <p className="text-muted mb-1" style={{ fontSize: "0.8125rem" }}>
            Un equipo no puede jugar en dos zonas de la misma rueda. Estos quedan sólo en la primera
            zona en la que aparecen; revisá si el nombre está bien resuelto.
          </p>
          {repetidos.map((r, i) => (
            <p key={i} className="text-warning mb-1" style={{ fontSize: "0.8125rem" }}>
              {r.nombreExcel} — ya está en {r.zona}
            </p>
          ))}
        </div>
      )}

      <div className="sub-section-title">Zonas a crear</div>
      {resolucion.map((zona) => {
        const final = porClave[zona.clave];
        return (
          <div className="card mb-2" key={zona.clave}>
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="font-weight-bold" style={{ fontSize: "0.875rem" }}>
                  {zona.nombre}
                </span>
                <span className="text-muted" style={{ fontSize: "0.8125rem" }}>
                  {final.teamIds.length} equipos
                </span>
              </div>
              {final.teamIds.length === 0 ? (
                <p className="text-muted mb-0" style={{ fontSize: "0.8125rem" }}>
                  Sin equipos reconocidos: esta zona no se crea.
                </p>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: "0.8125rem" }}>
                  {final.teamIds.map(nombreDeEquipo).join(" · ")}
                </p>
              )}
            </div>
          </div>
        );
      })}

      <div className="d-flex justify-content-end mt-3" style={{ gap: "0.5rem" }}>
        <Button className="btn-arhock btn-sm" style={{ opacity: 0.55 }} onClick={onBack}>
          Cancelar
        </Button>
        <Button
          className="btn-arhock btn-sm"
          onClick={confirmar}
          disabled={idsAImportar.length === 0 || confirmando}
        >
          {confirmando ? "Importando…" : "Importar estructura"}
        </Button>
      </div>
    </div>
  );
}
